import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import Cookies from 'js-cookie';
import { MenuItem, Select, FormControl, InputLabel, createTheme, ThemeProvider, CssBaseline, InputAdornment, Box, CircularProgress } from '@mui/material';
import { Button, Card, CardContent, Typography } from '@mui/material';
import { useHistory } from "react-router-dom";
import Autocomplete from '@material-ui/lab/Autocomplete';
import { Snackbar } from '@material-ui/core';

// Leaflet Imports
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

import api from '../../../../../services/api';
import NavegadorGestao from "../../../../../components/NavBarGestao";
import globalConfig from '../../../../../configs/globalConfigs';

// Icons
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import EngineeringIcon from '@mui/icons-material/Engineering';
import NotesIcon from '@mui/icons-material/Notes';
import MyLocationIcon from '@mui/icons-material/MyLocation';

const drawerWidth = 240;

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
    },
    appBarSpacer: theme.mixins.toolbar,
    content: {
        flexGrow: 1,
        height: '100vh',
        overflow: 'auto',
    },
    container: {
        paddingTop: theme.spacing(4),
        paddingBottom: theme.spacing(4),
    },
}));

const mapContainerStyle = {
    width: '100%',
    height: '400px',
    borderRadius: '8px',
    marginTop: '20px',
    zIndex: 1 // Ensure it stays below MUI dialogs/overlays if any
};

// Fix for Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const installationIcon = L.icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
});

const executorIcon = L.icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
});

export default function CadastrarRondaMotorizada() {
    const classes = useStyles();
    const history = useHistory();
    const [darkMode, setDarkMode] = useState(Cookies.get('darkMode') === 'true');

    // States
    const [loading, setLoading] = useState(true);
    const [instalacoes, setInstalacoes] = useState([]);
    const [instalacaoSelecionada, setInstalacaoSelecionada] = useState(null);
    const [descricao, setDescricao] = useState('');
    
    const [tipoSolicitante, setTipoSolicitante] = useState(0); // 0: Self, 10: User, 20: Client
    const [solicitantes, setSolicitantes] = useState([]);
    const [clientes, setClientes] = useState([]);
    const [solicitanteSelecionado, setSolicitanteSelecionado] = useState(null);
    
    const [executoresOnline, setExecutoresOnline] = useState([]);
    const [executorSelecionado, setExecutorSelecionado] = useState(null);

    const [mapCenter, setMapCenter] = useState({ lat: -15.7801, lng: -47.9292 }); // Default Brasília
    const [zoom, setZoom] = useState(4);

    const mapRef = useRef(null);
    const mapInstance = useRef(null);
    const markersLayerRef = useRef(L.layerGroup());
    const [mapInitialized, setMapInitialized] = useState(false);

    useEffect(() => {
        const handleDarkModeChange = () => {
            setDarkMode(Cookies.get('darkMode') === 'true');
        };
        window.addEventListener('darkModeChanged', handleDarkModeChange);
        return () => window.removeEventListener('darkModeChanged', handleDarkModeChange);
    }, []);

    const theme = useMemo(() => createTheme({
        palette: {
            mode: darkMode ? 'dark' : 'light',
            ...(darkMode && {
                background: {
                    default: '#121212',
                    paper: '#1e1e1e',
                },
                text: {
                    primary: '#fff',
                    secondary: '#ccc',
                },
            }),
        },
    }), [darkMode]);

    async function listarInformacoesIniciais() {
        try {
            const token = Cookies.get('token');
            const headers = { 'Authorization': 'Bearer ' + token };

            // Fetch Installations
            const resInst = await api.get("/v1/protected/clientes/instalacoes/listartodos", { headers });
            setInstalacoes(resInst.data);

            // Fetch Users (Solicitantes and Executors)
            const resUsers = await api.get("/v1/protected/user/listartodos", { headers });
            setSolicitantes(resUsers.data);
            
            // For now, filter executors who have lat/lng or are marked as online
            const online = resUsers.data.filter(u => u.status_ronda_motorizada_online === 1 || (u.latitude && u.longitude));
            setExecutoresOnline(online);

            // Fetch Clients
            const resClientes = await api.get("/v1/protected/clientes/listartodos", { headers });
            setClientes(resClientes.data);

            setLoading(false);
        } catch (_err) {
            console.log("Erro ao listar informações iniciais: " + _err);
            setLoading(false);
        }
    }

    useEffect(() => {
        listarInformacoesIniciais();
    }, []);

    // Initialize Map
    useEffect(() => {
        if (!mapRef.current || mapInstance.current) return;

        mapInstance.current = L.map(mapRef.current).setView([mapCenter.lat, mapCenter.lng], zoom);

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        }).addTo(mapInstance.current);

        markersLayerRef.current.addTo(mapInstance.current);
        setMapInitialized(true);

    }, [loading]);

    // Cleanup Map
    useEffect(() => {
        return () => {
            if (mapInstance.current) {
                mapInstance.current.remove();
                mapInstance.current = null;
            }
        };
    }, []);

    // Update markers and center
    useEffect(() => {
        if (!mapInstance.current || !mapInitialized) return;

        markersLayerRef.current.clearLayers();

        // Installation Marker
        if (instalacaoSelecionada && instalacaoSelecionada.latitude) {
            const pos = [parseFloat(instalacaoSelecionada.latitude), parseFloat(instalacaoSelecionada.longitude)];
            L.marker(pos, { icon: installationIcon })
                .addTo(markersLayerRef.current)
                .bindPopup("Instalação Selecionada");
            
            mapInstance.current.setView(pos, 15);
        } else {
            mapInstance.current.setView([mapCenter.lat, mapCenter.lng], zoom);
        }

        // Executors Markers
        executoresOnline.forEach(executor => {
            if (executor.latitude && executor.longitude) {
                const pos = [parseFloat(executor.latitude), parseFloat(executor.longitude)];
                L.marker(pos, { icon: executorIcon })
                    .addTo(markersLayerRef.current)
                    .bindPopup(`Executor: ${executor.nome_amigavel}`)
                    .on('click', () => {
                        setExecutorSelecionado(executor);
                    });
            }
        });

    }, [instalacaoSelecionada, executoresOnline]);

    const handleSetInstalacao = (instalacao) => {
        setInstalacaoSelecionada(instalacao);
        if (instalacao && instalacao.latitude && instalacao.longitude) {
            const pos = { lat: parseFloat(instalacao.latitude), lng: parseFloat(instalacao.longitude) };
            setMapCenter(pos);
            setZoom(15);
        }
    };

    const returnDescricaoInstalacao = (instalacao) => {
        if (!instalacao) return "";
        return `SIGMA: ${instalacao.identificador_integrador} | ${instalacao.tipo_instalacao === 0 ? (instalacao.nome + " " + instalacao.sobrenome) : instalacao.nome_fantasia}`;
    };

    const returnDescricaoUsuario = (usuario) => {
        if (!usuario) return "";
        return `${usuario.nome_amigavel} (${usuario.id_usuario})`;
    };

    const returnDescricaoCliente = (cliente) => {
        if (!cliente) return "";
        return `${cliente.nome_fantasia || (cliente.nome + " " + cliente.sobrenome)} (${cliente.id_cliente})`;
    };

    async function salvar() {
        if (!instalacaoSelecionada) { alert("Selecione a Instalação"); return; }
        if (!executorSelecionado) { alert("Selecione um Executor Online"); return; }
        if (descricao.length < 5) { alert("Informe uma descrição válida"); return; }

        try {
            const token = Cookies.get('token');
            const headers = { 'Authorization': 'Bearer ' + token };

            let solicitanteNome = "";
            if (tipoSolicitante === 0) {
                solicitanteNome = Cookies.get("nome_amigavel");
            } else if (solicitanteSelecionado) {
                solicitanteNome = tipoSolicitante === 10 
                    ? solicitanteSelecionado.nome_amigavel 
                    : (solicitanteSelecionado.nome_fantasia || (solicitanteSelecionado.nome + " " + solicitanteSelecionado.sobrenome));
            }

            const cadastro = {
                usuario_criador: { id_usuario: Cookies.get("id_usuario") },
                instalacao: { id_instalacao: instalacaoSelecionada.id_instalacao },
                usuario_executor: { id_usuario: executorSelecionado.id_usuario },
                descricao: descricao,
                tipo_solicitante: tipoSolicitante,
                solicitante_nome: solicitanteNome,
                data_hora_criacao: new Date().toISOString()
            };

            const response = await api.post('/v1/protected/rondamotorizada/cadastrar', cadastro, { headers });
            if (response.data) {
                alert("Ronda Motorizada Cadastrada!");
                history.push("/gestao/operacional/rondamotorizada/gerenciar/" + response.data.id_ronda_motorizada);
            }
        } catch (_err) {
            console.log("Erro ao cadastrar: " + _err);
            alert("Erro ao cadastrar Ronda Motorizada.");
        }
    }

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <div style={{ backgroundColor: theme.palette.background.default, color: theme.palette.text.primary, minHeight: '100vh', padding: '20px' }}>
                <NavegadorGestao
                    Operacional={true}
                    RondaMotorizada={true}
                    corFundo={theme.palette.background.paper}
                    travado={true}
                    corTexto={theme.palette.text.primary}
                />
                
                <Container maxWidth="lg" style={{ marginTop: '20px' }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                        <Typography variant="h5">Nova Ronda Motorizada</Typography>
                        <Button variant="contained" color="primary" onClick={salvar} size="large">
                            Salvar Ronda
                        </Button>
                    </Box>

                    {!loading && (
                        <Grid container spacing={4}>
                            {/* Form Section */}
                            <Grid item xs={12} md={6}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>Dados da Ronda</Typography>
                                        <Grid container spacing={3}>
                                            <Grid item xs={12}>
                                                <Autocomplete
                                                    options={instalacoes}
                                                    getOptionLabel={returnDescricaoInstalacao}
                                                    value={instalacaoSelecionada}
                                                    onChange={(e, v) => handleSetInstalacao(v)}
                                                    renderInput={(params) => (
                                                        <TextField {...params} label="Instalação" variant="outlined" fullWidth />
                                                    )}
                                                />
                                            </Grid>

                                            <Grid item xs={12}>
                                                <FormControl fullWidth variant="outlined">
                                                    <InputLabel>Tipo de Solicitante</InputLabel>
                                                    <Select
                                                        value={tipoSolicitante}
                                                        onChange={(e) => { setTipoSolicitante(e.target.value); setSolicitanteSelecionado(null); }}
                                                        label="Tipo de Solicitante"
                                                        startAdornment={<InputAdornment position="start"><PersonIcon /></InputAdornment>}
                                                    >
                                                        <MenuItem value={0}>Sou o solicitante</MenuItem>
                                                        <MenuItem value={10}>Usuário do Sistema</MenuItem>
                                                        <MenuItem value={20}>Cliente</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Grid>

                                            {tipoSolicitante === 10 && (
                                                <Grid item xs={12}>
                                                    <Autocomplete
                                                        options={solicitantes}
                                                        getOptionLabel={returnDescricaoUsuario}
                                                        value={solicitanteSelecionado}
                                                        onChange={(e, v) => setSolicitanteSelecionado(v)}
                                                        renderInput={(params) => (
                                                            <TextField {...params} label="Escolha o Usuário Solicitante" variant="outlined" />
                                                        )}
                                                    />
                                                </Grid>
                                            )}

                                            {tipoSolicitante === 20 && (
                                                <Grid item xs={12}>
                                                    <Autocomplete
                                                        options={clientes}
                                                        getOptionLabel={returnDescricaoCliente}
                                                        value={solicitanteSelecionado}
                                                        onChange={(e, v) => setSolicitanteSelecionado(v)}
                                                        renderInput={(params) => (
                                                            <TextField {...params} label="Escolha o Cliente Solicitante" variant="outlined" />
                                                        )}
                                                    />
                                                </Grid>
                                            )}

                                            <Grid item xs={12}>
                                                <Autocomplete
                                                    options={executoresOnline}
                                                    getOptionLabel={returnDescricaoUsuario}
                                                    value={executorSelecionado}
                                                    onChange={(e, v) => setExecutorSelecionado(v)}
                                                    renderInput={(params) => (
                                                        <TextField 
                                                            {...params} 
                                                            label="Executor Online" 
                                                            variant="outlined" 
                                                            InputProps={{
                                                                ...params.InputProps,
                                                                startAdornment: (
                                                                    <InputAdornment position="start"><EngineeringIcon /></InputAdornment>
                                                                )
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </Grid>

                                            <Grid item xs={12}>
                                                <TextField
                                                    label="Descrição / Observações"
                                                    multiline
                                                    rows={4}
                                                    variant="outlined"
                                                    fullWidth
                                                    value={descricao}
                                                    onChange={(e) => setDescricao(e.target.value)}
                                                    placeholder="Descreva o motivo ou detalhes da ronda..."
                                                />
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Map Section */}
                            <Grid item xs={12} md={6}>
                                <Card style={{ height: '100%' }}>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>Visualização no Mapa</Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            Localização da instalação (azul) e executores online (verde).
                                        </Typography>
                                        <div 
                                            ref={mapRef} 
                                            style={mapContainerStyle}
                                        />
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    )}
                </Container>
            </div>
        </ThemeProvider>
    );
}
